import React, { useRef } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { Paprika, PaprikaGradients } from '../../../../styles/theme';
import { useGroceryBannerState } from '../../useGroceryBannerState';
import { nextMealEyebrowKeys, offersCookNow } from '../nextMealLabel';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import { entrySub, entryTitle, type MealEntry } from '../../../../redux/mealEntry';

interface Props {
  recipe: RecipeIndexEntry;
  /** Day index of the meal, Monday = 0. */
  targetDi: number;
  /** Day index of today, Monday = 0. */
  todayDi: number;
  /** Meal index — 0 breakfast, 1 lunch, 2 dinner. */
  mi: number;
  servings?: number;
  /** Opens the recipe detail drawer — the card body is the target. */
  onPress: () => void;
  /** The primary action — opens the full recipe, ready to cook. */
  onCook: () => void;
}

/**
 * Desktop "next meal" bar (#579).
 *
 * Occupies exactly the band the grocery banner used to own, so it costs no
 * vertical space — but it carries the answer to "what am I cooking?" instead of
 * a shopping total the user doesn't act on from this screen. The grocery count
 * survives as a chip on the right: still one click away, no longer the loudest
 * thing above the grid.
 *
 * The mobile twin is NextMealHero; both read the same `findNextMealTarget` and
 * the same eyebrow keys, so the two platforms can't drift on what "next" means.
 */
export function NextMealBar({
  recipe,
  targetDi,
  todayDi,
  mi,
  servings,
  onPress,
  onCook,
}: Props) {
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'desktop-bar');
  const grocery = useGroceryBannerState();
  const { dayKey, mealKey } = nextMealEyebrowKeys(targetDi, todayDi, mi);
  const eyebrow = `${t(dayKey)} · ${t(mealKey)}`;
  const showCook = offersCookNow(targetDi, todayDi);

  const meta = [
    recipe.readyInMinutes ? t('thisWeek.minutesShort', { count: recipe.readyInMinutes }) : null,
    servings != null ? t('thisWeek.servingsShort', { count: servings }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={styles.bar} testID="next-meal-bar">
      <Pressable
        style={({ pressed }) => [styles.body, pressed && styles.bodyPressed]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${eyebrow} — ${title}`}
      >
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.photo}
          resizeMode="cover"
          fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
        />
        <View style={styles.text}>
          <Text style={styles.eyebrow} numberOfLines={1}>
            {eyebrow.toUpperCase()}
          </Text>
          <Text style={styles.title} numberOfLines={1} testID="planned-meal-title">
            {title}
          </Text>
          {meta ? (
            <Text style={styles.meta} numberOfLines={1}>
              {meta}
            </Text>
          ) : null}
        </View>
      </Pressable>

      {/* Grocery count — demoted to a chip, still reachable in one click. */}
      {grocery.recipeMealCount > 0 && (
        <Pressable
          style={({ pressed }) => [styles.groceryChip, pressed && styles.chipPressed]}
          onPress={grocery.onPress}
          accessibilityRole="button"
          accessibilityLabel={grocery.label}
        >
          <View
            style={[
              styles.dot,
              grocery.allCovered && !grocery.isOtherWeek && styles.dotDone,
            ]}
          />
          <Text style={styles.groceryText} numberOfLines={1}>
            {grocery.label}
          </Text>
          <Feather name="chevron-right" size={15} color={Paprika.textMuted} />
        </Pressable>
      )}

      {/* Only for a meal later today; the bar still opens a later one. */}
      {showCook && (
        <Pressable
          style={({ pressed }) => [styles.ctaWrap, pressed && styles.chipPressed]}
          onPress={onCook}
          accessibilityRole="button"
          accessibilityLabel={t('thisWeek.cookNow')}
          testID="next-meal-bar-cook"
        >
          <LinearGradient
            colors={PaprikaGradients.cookCta as [string, string]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.cta}
          >
            <Feather name="book-open" size={16} color="#ffffff" />
            <Text style={styles.ctaText}>{t('thisWeek.cookNow')}</Text>
          </LinearGradient>
        </Pressable>
      )}
    </View>
  );
}

interface EntryBarProps {
  entry: MealEntry;
  resolveTitle: (recipeId: string) => string | undefined;
  /** Day index of the meal, Monday = 0. */
  targetDi: number;
  /** Day index of today, Monday = 0. */
  todayDi: number;
  /** Meal index — 0 breakfast, 1 lunch, 2 dinner. */
  mi: number;
  /** Opens the slot's menu, anchored on whatever was pressed. */
  onChange: (anchor: { x: number; y: number; width: number; height: number }) => void;
}

/**
 * The bar when the next planned meal is not a recipe — a simple meal, a restaurant,
 * leftovers (`findNextEntryTarget`). Same band and same eyebrow as the recipe
 * bar, so the cook reads « Aujourd'hui · Souper » or « Demain · Souper »
 * above what they said they would eat instead of Saturday's breakfast. Nothing to cook, so no « Cuisiner »:
 * the action is « Changer », which opens the slot's menu.
 */
export function NextEntryBar({ entry, resolveTitle, targetDi, todayDi, mi, onChange }: EntryBarProps) {
  const { t } = useTranslation();
  const grocery = useGroceryBannerState();
  const { dayKey, mealKey } = nextMealEyebrowKeys(targetDi, todayDi, mi);
  const eyebrow = `${t(dayKey)} · ${t(mealKey)}`;
  const title = entryTitle(entry, resolveTitle, t);
  const meta = ENTRY_META[entry.type];
  const changeRef = useRef<View>(null);

  const openMenu = () => {
    const el = changeRef.current;
    if (!el) return;
    el.measureInWindow((x, y, width, height) => onChange({ x, y, width, height }));
  };

  return (
    <View style={styles.bar} testID="next-entry-bar">
      <Pressable
        style={({ pressed }) => [styles.body, pressed && styles.bodyPressed]}
        onPress={openMenu}
        accessibilityRole="button"
        accessibilityLabel={`${eyebrow} — ${title}`}
      >
        <View style={[styles.photo, styles.entryIcon, { backgroundColor: meta.tint }]}>
          <EntryIcon type={entry.type} size={24} />
        </View>
        <View style={styles.text}>
          <Text style={styles.eyebrow} numberOfLines={1}>
            {eyebrow.toUpperCase()}
          </Text>
          <Text style={styles.title} numberOfLines={1} testID="next-entry-title">
            {title}
          </Text>
          <Text style={styles.meta} numberOfLines={1}>
            {entrySub(entry, t)}
          </Text>
        </View>
      </Pressable>

      {grocery.recipeMealCount > 0 && (
        <Pressable
          style={({ pressed }) => [styles.groceryChip, pressed && styles.chipPressed]}
          onPress={grocery.onPress}
          accessibilityRole="button"
          accessibilityLabel={grocery.label}
        >
          <View
            style={[
              styles.dot,
              grocery.allCovered && !grocery.isOtherWeek && styles.dotDone,
            ]}
          />
          <Text style={styles.groceryText} numberOfLines={1}>
            {grocery.label}
          </Text>
          <Feather name="chevron-right" size={15} color={Paprika.textMuted} />
        </Pressable>
      )}

      <Pressable
        ref={changeRef}
        style={({ pressed }) => [styles.changeBtn, pressed && styles.chipPressed]}
        onPress={openMenu}
        accessibilityRole="button"
        testID="next-entry-change"
      >
        <Feather name="edit-2" size={14} color={Paprika.textPrimary} />
        <Text style={styles.changeText}>{t('thisWeek.changeMeal')}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginTop: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 18,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.10)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  // Takes the slack so the chip and CTA stay pinned right, and lets a long
  // title ellipsise on its one line instead of shoving them off the row.
  body: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  bodyPressed: {
    opacity: 0.92,
  },
  // Kept small on purpose: every pixel of this bar is a pixel the grid's open
  // row does not get (rowFocus.ts).
  photo: {
    width: 56,
    height: 56,
    borderRadius: 10,
    backgroundColor: Paprika.bgSunk,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    letterSpacing: 0.9,
    color: Paprika.primary,
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 21,
    lineHeight: 26,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  meta: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  groceryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    paddingVertical: 9,
    paddingHorizontal: 15,
    borderRadius: 100,
    backgroundColor: Paprika.surfaceLow,
  },
  chipPressed: {
    opacity: 0.9,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: Paprika.primary,
  },
  dotDone: {
    backgroundColor: Paprika.success,
  },
  groceryText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.textSecondary,
    flexShrink: 1,
  },
  ctaWrap: {
    borderRadius: 26,
    overflow: 'hidden',
    flexShrink: 0,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    paddingVertical: 11,
    paddingHorizontal: 24,
  },
  ctaText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14.5,
    color: '#ffffff',
  },
  entryIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  changeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    flexShrink: 0,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 26,
    borderWidth: 1,
    borderColor: 'rgba(133, 116, 107, 0.35)',
  },
  changeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.textPrimary,
  },
});

export default NextMealBar;
